import { cn } from '../../utilities/cn';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Loader } from '@firmable/icons';

// Variants on cva, the shadcn way. The component merges the result with `cn`, so a compound class replaces the base class it conflicts with.
const base =
  'flex items-center justify-center transition-colors duration-300 cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-stroke-interactive';

export const buttonStyles = cva(base, {
  variants: {
    variant: {
      primary: 'text-content-lighter bg-surface-interactive border border-transparent',
      secondary: 'text-content-dark bg-surface-white border border-stroke-light',
      inverse: 'text-surface-white bg-transparent border border-surface-white focus-visible:ring-surface-white',
      tertiary: 'text-content-interactive bg-transparent border border-transparent',
      destructive: 'text-danger-dark bg-transparent border border-transparent',
      premiumPurple: 'text-accent-purple-fg bg-surface-white border border-accent-purple-fg focus-visible:ring-accent-purple-fg',
    },
    size: {
      sm: 'rounded-radius-sm py-xs px-sm gap-xs !text-label-small',
      lg: 'rounded-radius-md py-sm px-lg gap-sm !text-label-large',
    },
    selected: {
      true: '',
      false: '',
    },
    disabled: {
      true: 'opacity-50 hover:cursor-not-allowed',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'sm',
  },

  compoundVariants: [
    // Hover State Variants
    {
      variant: 'primary',
      disabled: false,
      className: 'hover:bg-surface-interactive-dark',
    },
    {
      variant: 'secondary',
      disabled: false,
      className: 'hover:bg-surface-interactive-lighter hover:border-stroke-interactive hover:text-content-interactive',
    },
    {
      variant: 'inverse',
      disabled: false,
      className: 'hover:bg-surface-white/10',
    },
    {
      variant: 'tertiary',
      disabled: false,
      className: 'hover:text-content-interactive hover:bg-surface-interactive-lighter',
    },
    {
      variant: 'destructive',
      disabled: false,
      className: 'hover:bg-danger-light',
    },
    {
      variant: 'premiumPurple',
      disabled: false,
      className: 'hover:bg-accent-purple-muted hover:border-accent-purple-fg hover:text-accent-purple-fg',
    },

    // Selected State Variants
    {
      variant: 'primary',
      selected: true,
      className: 'bg-surface-interactive-dark text-content-lighter',
    },
    {
      variant: 'secondary',
      selected: true,
      className: 'bg-surface-interactive-lighter border-stroke-interactive text-content-interactive',
    },
    {
      variant: 'inverse',
      selected: true,
      className: 'bg-surface-white/10',
    },
    {
      variant: 'tertiary',
      selected: true,
      className: 'text-content-interactive bg-surface-interactive-lighter',
    },
    {
      variant: 'destructive',
      selected: true,
      className: 'bg-danger-light',
    },
    {
      variant: 'premiumPurple',
      selected: true,
      className: 'bg-accent-purple-muted',
    },

    //selected and disabled state variants
    {
      variant: 'primary',
      selected: true,
      disabled: true,
      className: 'bg-surface-interactive-dark text-content-lighter',
    },
    {
      variant: 'secondary',
      selected: true,
      disabled: true,
      className: 'bg-surface-interactive-lighter border-stroke-interactive text-content-interactive',
    },
    {
      variant: 'inverse',
      selected: true,
      disabled: true,
      className: 'bg-surface-white/10',
    },
    {
      variant: 'tertiary',
      selected: true,
      disabled: true,
      className: 'text-content-interactive bg-surface-interactive-lighter',
    },
    {
      variant: 'destructive',
      selected: true,
      disabled: true,
      className: 'bg-danger-light',
    },
    {
      variant: 'premiumPurple',
      selected: true,
      disabled: true,
      className: 'bg-accent-purple-muted',
    },
  ],
});

// Button props extends VariantProps from cva
// `disabled` comes from the button attributes; cva's own type for it also allows null.
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof buttonStyles>, 'disabled'> {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
  /**
   * When set, the button is rendered as disabled and wrapped in a Tooltip
   * with this string as content. Internally renders as a <div> to avoid the
   * "native disabled buttons don't fire mouse events" issue that breaks
   * Tooltip hover detection.
   */
  disabledReason?: string;
  /**
   * Loading state (PRD-940): the label and icons hide, a centred spinner shows,
   * and the button keeps the exact width it had when loading started, so nothing
   * beside it moves. The button ignores clicks while loading but keeps its look
   * (no disabled dimming).
   */
  loading?: boolean;
  /**
   * Success tick, opt in: when this turns true the button shows a tick for 800ms,
   * then the label returns. Only for buttons that stay on screen after the action;
   * a button whose success closes a modal or navigates should not set it.
   */
  success?: boolean;
}

const TICK_MS = 800;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, leftIcon, rightIcon, children, variant, size, disabled = false, selected = false, disabledReason, loading, success, onClick, style, ...rest },
    ref,
  ) => {
    const isDisabled = disabled || !!disabledReason;
    const variants = useMemo(
      () => buttonStyles({ variant, size, disabled: isDisabled, selected }),
      [variant, size, isDisabled, selected],
    );

    const innerRef = useRef<HTMLButtonElement | null>(null);
    const idleWidth = useRef<number | null>(null);
    const wasBusy = useRef(false);
    const [showTick, setShowTick] = useState(false);

    useEffect(() => {
      if (!success) return undefined;
      setShowTick(true);
      const timer = setTimeout(() => setShowTick(false), TICK_MS);
      return () => clearTimeout(timer);
    }, [success]);

    // Only a button given `loading` or `success` gets the inner label layer and `relative`. Every other button
    // keeps its children as direct children, so a button laid out by its own className (a card) keeps its
    // layout, and a click on its label still lands on the button.
    const canBusy = loading !== undefined || success !== undefined;
    const isBusy = !!loading || showTick;
    if (isBusy) wasBusy.current = true;

    // The width lock: remember the idle width so the button cannot change size
    // while its content is a spinner or a tick (layout stays put). Read the computed width: offsetWidth rounds
    // (a label 0.13px wider than the lock wraps onto a second line) and getBoundingClientRect includes a
    // parent's transform (a dialog scaling in reads small).
    useLayoutEffect(() => {
      if (!canBusy || isBusy || !innerRef.current) return;
      const width = parseFloat(getComputedStyle(innerRef.current).width);
      if (Number.isFinite(width)) idleWidth.current = width;
    });

    const setRefs = (node: HTMLButtonElement | null) => {
      innerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    };

    const busyStyle = isBusy && idleWidth.current ? { ...style, width: idleWidth.current } : style;
    const spinnerSize = size === 'lg' ? 20 : 16;

    const label = (
      <>
        {leftIcon && <span>{leftIcon}</span>}
        {children}
        {rightIcon && <span>{rightIcon}</span>}
      </>
    );

    const content = !canBusy ? (
      label
    ) : (
      <>
        <span
          className={cn('flex items-center justify-center gap-[inherit]', {
            // nowrap: the hidden label can never add a line under the spinner, so the height holds too
            'invisible whitespace-nowrap': isBusy,
            'animate-popIn motion-reduce:animate-none': !isBusy && wasBusy.current,
          })}>
          {label}
        </span>
        {loading && (
          <span className="absolute inset-0 flex items-center justify-center animate-popIn motion-reduce:animate-none" aria-hidden="true">
            <Loader.Circle size={spinnerSize} />
          </span>
        )}
        {!loading && showTick && (
          <span className="absolute inset-0 flex items-center justify-center animate-popIn motion-reduce:animate-none" aria-hidden="true">
            <svg width={spinnerSize} height={spinnerSize} viewBox="0 0 20 20" fill="none">
              <path
                d="M4 10.5l4 4 8-8.5"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="animate-tickDraw motion-reduce:animate-none [stroke-dasharray:20] [stroke-dashoffset:20] motion-reduce:[stroke-dashoffset:0]"
              />
            </svg>
          </span>
        )}
      </>
    );

    if (disabledReason) {
      return (
        <Tooltip.Provider delayDuration={200}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <div role="button" aria-disabled className={cn(variants, { relative: canBusy }, className)}>
                {content}
              </div>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className="px-sm py-xs rounded-radius-sm bg-surface-darker text-white text-body-small shadow-raised z-50"
                sideOffset={4}>
                {disabledReason}
                <Tooltip.Arrow className="fill-surface-darker" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      );
    }

    return (
      <button
        ref={setRefs}
        disabled={isDisabled}
        className={cn(variants, { relative: canBusy }, className)}
        style={busyStyle}
        aria-busy={isBusy || undefined}
        // While busy the click is cancelled rather than ignored: Enter in a form field fires a click on the
        // form's submit button, and only a cancelled click (or a disabled button) stops a second submit.
        onClick={isDisabled ? undefined : isBusy ? (event) => event.preventDefault() : onClick}
        {...rest}>
        {content}
      </button>
    );
  },
);

Button.displayName = 'Button';
